import { _ as _extends } from './extends-3e8335ef.esm.js';
import React__default, { useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { View, StyleSheet } from 'react-native';
import { useBlurOnFulfill, useClearByFocusCell, CodeField, Cursor } from 'react-native-confirmation-code-field';
import { u as useTheme } from './useTheme-c05a5bd5.esm.js';
import { A as AppText } from './index-48b066c3.esm.js';

// interface AppCodeInputProps {
//   /**
//    * Sets the main caption to describe the choice the user has to make
//    */
//   caption?: string;
//   /**
//    * Sets the sub caption to describe the choice the user has to make in details
//    */
//   subCaption?: string;
//   /**
//    * Background colour of the button used for the cancellation
//    */
//   cancelButtonColor?: string;
//   /**
//    * Label of the button used for the cancellation
//    */
//   cancelButtonLabel?: string;
//   /**
//    * Event raised when the user presses the cancel button
//    */
//   onCancelButtonPress?: () => void;
//   /**
//    * Background colour of the button used for the confirmation
//    */
//   confirmButtonColor?: string;
//   /**
//    * Label of the button used for the confirmation
//    */
//   confirmButtonLabel?: string;
//   /**
//    * Style applied to the caption text
//    */
//   captionStyle?: StyleProp<ViewStyle>;
//   /**
//    * Style applied to the sub-caption text
//    */
//   subCaptionStyle?: StyleProp<ViewStyle>;
//   /**
//    * Style applied to the caption wrapper of the underlying AppText
//    */
//   captionWrapperStyle?: StyleProp<ViewStyle>;
//   /**
//    * Style applied to the sub-caption wrapper of the underlying AppText
//    */
//   subCaptionWrapperStyle?: StyleProp<ViewStyle>;
//   /**
//    * Style applied to wrapper of all sub-components
//    */
//   wrapperStyle?: StyleProp<ViewStyle>;
//   rootStyle?: StyleProp<ViewStyle>;
//   AppChoiceButton?: any;
//   confirmCaption?: string;
//   cancelCaption?: string;
//   children?: React.ReactNode;
//   value?: string;
//   setValue?: (value: string) => void;
//   cellCount?: number;
//   numbersOnly?: boolean;
// }

const AppCodeInput = _ref => {
  let {
    caption,
    subCaption,
    cancelButtonColor,
    cancelButtonLabel = "Cancel",
    confirmButtonColor,
    confirmButtonLabel = "Confirm",
    captionStyle,
    subCaptionStyle,
    captionWrapperStyle,
    subCaptionWrapperStyle,
    wrapperStyle,
    confirmCaption,
    cancelCaption,
    children,
    value,
    setValue,
    cellCount = 6,
    rootStyle,
    numbersOnly = true,
    ...otherProps
  } = _ref;
  const ref = useBlurOnFulfill({
    value,
    cellCount
  });
  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value,
    setValue
  });
  const {
    currentTheme,
    mode
  } = useTheme();
  const styles = generateStyles(currentTheme);
  useTranslation();
  useEffect(() => {
    if (!value) return;
    let newValue = value;
    let shouldUpdate = false;
    if (newValue.length > cellCount) {
      newValue = newValue.slice(0, cellCount);
      shouldUpdate = true;
    }
    if (numbersOnly && !/^[0-9]*$/.test(newValue)) {
      newValue = newValue.replace(/[^0-9]/g, "");
      shouldUpdate = true;
    }
    if (shouldUpdate && newValue !== value) {
      setValue(newValue);
    }
  }, [value, cellCount, numbersOnly]);
  return /*#__PURE__*/React__default.createElement(View, {
    style: [styles.wrapper, wrapperStyle]
  }, /*#__PURE__*/React__default.createElement(AppText, {
    wrapperStyle: [styles.captionWrapper, captionWrapperStyle],
    style: [styles.caption, captionStyle]
  }, caption), /*#__PURE__*/React__default.createElement(AppText, {
    wrapperStyle: [styles.subCaptionWrapper, subCaptionWrapperStyle],
    style: [styles.subCaption, subCaptionStyle]
  }, subCaption), /*#__PURE__*/React__default.createElement(CodeField, _extends({
    ref: ref
  }, props, {
    value: value,
    onChangeText: setValue,
    cellCount: cellCount,
    rootStyle: [styles.codeFieldRoot, rootStyle],
    keyboardType: "number-pad",
    textContentType: "oneTimeCode",
    autoCompleteType: "password",
    importantForAutofill: "no",
    renderCell: _ref2 => {
      let {
        index,
        symbol,
        isFocused
      } = _ref2;
      return /*#__PURE__*/React__default.createElement(Fragment, {
        key: index
      }, /*#__PURE__*/React__default.createElement(AppText, {
        preventTranslation: true,
        key: `value-${index}`,
        color: currentTheme?.theme?.font_color,
        style: [styles.cell, isFocused && styles.focusCell],
        onLayout: getCellOnLayoutHandler(index)
      }, symbol || (isFocused ? /*#__PURE__*/React__default.createElement(Cursor, null) : null)), index === 2 ? /*#__PURE__*/React__default.createElement(View, {
        key: `separator-${index}`,
        style: styles.separator
      }) : null);
    }
  })));
};
const generateStyles = props => {
  return StyleSheet.create({
    wrapper: {
      maxWidth: 400,
      justifyContent: "center"
    },
    caption: {
      fontFamily: "boldFont",
      fontSize: props?.theme?.large_font_size
    },
    captionWrapper: {
      justifyContent: "flex-start"
    },
    subCaptionWrapper: {
      justifyContent: "flex-start",
      paddingTop: 10
    },
    subCaption: {
      fontSize: props?.theme?.small_font_size
    },
    choiceView: {
      paddingTop: 0,
      alignItems: "center"
    },
    root: {
      padding: 20,
      minHeight: 300
    },
    title: {
      textAlign: "center",
      fontSize: 30
    },
    codeFieldRoot: {
      marginTop: 20,
      width: 320,
      alignSelf: "center"
    },
    cell: {
      width: 40,
      height: 45,
      lineHeight: 40,
      fontSize: props?.theme?.large_font_size,
      color: props?.theme?.font_color,
      backgroundColor: props?.theme?.input_background_color || props?.theme?.background_color || "rgba(255,255,255,0.1)",
      borderWidth: 2,
      borderRadius: 8,
      borderColor: props?.theme?.border_color,
      textAlign: "center"
    },
    separator: {
      height: 2,
      width: 10,
      backgroundColor: props?.theme?.border_color,
      alignSelf: "center"
    },
    focusCell: {
      borderColor: props?.theme?.primary_color || props?.theme?.accent_color,
      backgroundColor: props?.theme?.focus_background_color || props?.theme?.input_background_color || "rgba(255,255,255,0.2)"
    }
  });
};
AppCodeInput.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "AppCodeInput",
  "props": {
    "cancelButtonLabel": {
      "defaultValue": {
        "value": "\"Cancel\"",
        "computed": false
      },
      "required": false
    },
    "confirmButtonLabel": {
      "defaultValue": {
        "value": "\"Confirm\"",
        "computed": false
      },
      "required": false
    },
    "cellCount": {
      "defaultValue": {
        "value": "6",
        "computed": false
      },
      "required": false
    },
    "numbersOnly": {
      "defaultValue": {
        "value": "true",
        "computed": false
      },
      "required": false
    }
  }
};

export { AppCodeInput as A };
